import type { SudokuGrid, Difficulty, CellValue } from '../../../../shared/types';
import { DIFFICULTY_CONFIG } from '../../../../shared/types';
import { shuffleArray } from './utils';

/**
 * Sudoku Generator
 * Generates valid Sudoku puzzles with configurable difficulty
 */
export class SudokuGenerator {
  private readonly SIZE = 9;
  private readonly BOX_SIZE = 3;

  /**
   * Generate a complete Sudoku puzzle with solution
   */
  generate(difficulty: Difficulty, seed?: number): { puzzle: SudokuGrid; solution: SudokuGrid } {
    // Generate a complete valid Sudoku
    const solution = this.generateComplete(seed);
    
    // Create puzzle by removing numbers
    const puzzle = this.createPuzzle(solution, difficulty, seed);
    
    return { puzzle, solution };
  }

  /**
   * Generate a complete valid 9x9 Sudoku grid
   */
  private generateComplete(seed?: number): SudokuGrid {
    const grid: SudokuGrid = Array(this.SIZE)
      .fill(0)
      .map(() => Array(this.SIZE).fill(0)) as SudokuGrid;

    this.fillGrid(grid, seed);
    return grid;
  }

  /**
   * Fill the grid using backtracking with randomization
   */
  private fillGrid(grid: SudokuGrid, seed?: number): boolean {
    const emptyCell = this.findEmptyCell(grid);
    
    if (!emptyCell) {
      return true; // Grid is complete
    }

    const { row, col } = emptyCell;
    
    // Get numbers 1-9 in random order
    const numbers = shuffleArray([1, 2, 3, 4, 5, 6, 7, 8, 9], seed);

    for (const num of numbers) {
      if (this.isValidPlacement(grid, row, col, num as CellValue)) {
        grid[row][col] = num as CellValue;

        if (this.fillGrid(grid, seed)) {
          return true;
        }

        grid[row][col] = 0; // Backtrack
      }
    }

    return false;
  }

  /**
   * Create puzzle by removing numbers from complete grid
   */
  private createPuzzle(solution: SudokuGrid, difficulty: Difficulty, seed?: number): SudokuGrid {
    const puzzle = solution.map(row => [...row]) as SudokuGrid;
    const config = DIFFICULTY_CONFIG[difficulty];
    
    // Calculate how many cells to remove
    const totalCells = this.SIZE * this.SIZE;
    const cluesCount = this.getRandomInRange(config.minClues, config.maxClues, seed);
    const cellsToRemove = totalCells - cluesCount;

    // Get all cell positions
    const positions: Array<[number, number]> = [];
    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        positions.push([row, col]);
      }
    }

    // Shuffle positions
    const shuffledPositions = shuffleArray(positions, seed);

    // Remove cells while maintaining unique solution
    let removed = 0;
    for (const [row, col] of shuffledPositions) {
      if (removed >= cellsToRemove) break;

      const backup = puzzle[row][col];
      puzzle[row][col] = 0;

      // Check if puzzle still has unique solution
      if (this.hasUniqueSolution(puzzle)) {
        removed++;
      } else {
        puzzle[row][col] = backup; // Restore if multiple solutions
      }
    }

    return puzzle;
  }

  /**
   * Check if puzzle has a unique solution
   */
  private hasUniqueSolution(grid: SudokuGrid): boolean {
    const solutions: SudokuGrid[] = [];
    this.countSolutions(grid.map(row => [...row]) as SudokuGrid, solutions, 2);
    return solutions.length === 1;
  }

  /**
   * Count number of solutions (stop at maxCount)
   */
  private countSolutions(grid: SudokuGrid, solutions: SudokuGrid[], maxCount: number): void {
    if (solutions.length >= maxCount) return;

    const emptyCell = this.findEmptyCell(grid);
    
    if (!emptyCell) {
      solutions.push(grid.map(row => [...row]) as SudokuGrid);
      return;
    }

    const { row, col } = emptyCell;

    for (let num = 1; num <= 9; num++) {
      if (this.isValidPlacement(grid, row, col, num as CellValue)) {
        grid[row][col] = num as CellValue;
        this.countSolutions(grid, solutions, maxCount);
        grid[row][col] = 0;
      }
    }
  }

  /**
   * Find first empty cell (with value 0)
   */
  private findEmptyCell(grid: SudokuGrid): { row: number; col: number } | null {
    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        if (grid[row][col] === 0) {
          return { row, col };
        }
      }
    }
    return null;
  }

  /**
   * Check if placing a number at position is valid
   */
  private isValidPlacement(grid: SudokuGrid, row: number, col: number, num: CellValue): boolean {
    // Check row
    for (let x = 0; x < this.SIZE; x++) {
      if (grid[row][x] === num) return false;
    }

    // Check column
    for (let x = 0; x < this.SIZE; x++) {
      if (grid[x][col] === num) return false;
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / this.BOX_SIZE) * this.BOX_SIZE;
    const boxCol = Math.floor(col / this.BOX_SIZE) * this.BOX_SIZE;

    for (let i = 0; i < this.BOX_SIZE; i++) {
      for (let j = 0; j < this.BOX_SIZE; j++) {
        if (grid[boxRow + i][boxCol + j] === num) return false;
      }
    }

    return true;
  }

  /**
   * Get random number in range
   */
  private getRandomInRange(min: number, max: number, seed?: number): number {
    if (seed !== undefined) {
      // Simple seeded random
      const x = Math.sin(seed++) * 10000;
      const random = x - Math.floor(x);
      return Math.floor(random * (max - min + 1)) + min;
    }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Validate if a Sudoku grid is completely valid
   */
  validate(grid: SudokuGrid): boolean {
    // Check all rows, columns, and boxes
    for (let i = 0; i < this.SIZE; i++) {
      if (!this.isValidGroup(this.getRow(grid, i))) return false;
      if (!this.isValidGroup(this.getColumn(grid, i))) return false;
    }

    for (let boxRow = 0; boxRow < this.SIZE; boxRow += this.BOX_SIZE) {
      for (let boxCol = 0; boxCol < this.SIZE; boxCol += this.BOX_SIZE) {
        if (!this.isValidGroup(this.getBox(grid, boxRow, boxCol))) return false;
      }
    }

    return true;
  }

  /**
   * Check if a group (row/column/box) is valid
   */
  private isValidGroup(group: CellValue[]): boolean {
    const seen = new Set<number>();
    for (const num of group) {
      if (num === 0) continue; // Skip empty cells
      if (seen.has(num)) return false;
      seen.add(num);
    }
    return true;
  }

  /**
   * Get row from grid
   */
  private getRow(grid: SudokuGrid, row: number): CellValue[] {
    return grid[row];
  }

  /**
   * Get column from grid
   */
  private getColumn(grid: SudokuGrid, col: number): CellValue[] {
    return grid.map(row => row[col]);
  }

  /**
   * Get 3x3 box from grid
   */
  private getBox(grid: SudokuGrid, startRow: number, startCol: number): CellValue[] {
    const box: CellValue[] = [];
    const boxRow = Math.floor(startRow / this.BOX_SIZE) * this.BOX_SIZE;
    const boxCol = Math.floor(startCol / this.BOX_SIZE) * this.BOX_SIZE;

    for (let i = 0; i < this.BOX_SIZE; i++) {
      for (let j = 0; j < this.BOX_SIZE; j++) {
        box.push(grid[boxRow + i][boxCol + j]);
      }
    }

    return box;
  }

  /**
   * Check if the current grid state is complete and valid
   */
  isComplete(grid: SudokuGrid): boolean {
    // Check no empty cells
    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        if (grid[row][col] === 0) return false;
      }
    }

    // Check validity
    return this.validate(grid);
  }

  /**
   * Get possible candidates for a cell
   */
  getCandidates(grid: SudokuGrid, row: number, col: number): CellValue[] {
    if (grid[row][col] !== 0) return [];

    const candidates: CellValue[] = [];
    
    for (let num = 1; num <= 9; num++) {
      if (this.isValidPlacement(grid, row, col, num as CellValue)) {
        candidates.push(num as CellValue);
      }
    }

    return candidates;
  }
}


